import { Text, View } from 'react-native';
import Svg, { Circle, Text as SvgText } from 'react-native-svg';

import { Fonts } from '@/constants/theme';
import { SAMPLE_DATA } from '@/constants/design-sample-data';

// Colors from the ai-template dark academia CSS
const C = {
  bg: '#1a1a2e',
  gold: '#c9a96e',
  goldMuted: '#a89270',
  text: '#d4c5a0',
  textDim: '#7a6f5d',
  textDark: '#5e5648',
  parchment: '#faf3e3',
  parchmentText: '#2a2520',
  parchmentMuted: '#8a7e6e',
  parchmentDark: '#5a5045',
  parchmentAuthor: '#6a6055',
  discussionBg: 'rgba(201, 169, 110, 0.04)',
  discussionBody: '#b0a590',
  discussionTitle: '#e0d5c0',
  sidebarBg: 'rgba(15, 15, 25, 0.4)',
  borderGold: 'rgba(201, 169, 110, 0.2)',
  borderGoldSubtle: 'rgba(201, 169, 110, 0.15)',
  borderGoldFaint: 'rgba(201, 169, 110, 0.1)',
  borderGoldAccent: 'rgba(201, 169, 110, 0.3)',
  stamp: '#8b0000',
};

function SectionHeading({ children }: { children: string }) {
  return (
    <View
      style={{
        borderBottomWidth: 1,
        borderBottomColor: C.borderGold,
        marginBottom: 24,
        paddingBottom: 12,
      }}
    >
      <Text
        style={{
          fontFamily: Fonts?.serifSemiBold,
          fontSize: 22,
          color: C.gold,
          letterSpacing: 1.2,
        }}
      >
        <Text style={{ opacity: 0.6, marginRight: 8 }}>{'\u00A7'} </Text>
        {children}
      </Text>
    </View>
  );
}

function SidebarHeading({ children }: { children: string }) {
  return (
    <View
      style={{
        borderBottomWidth: 1,
        borderBottomColor: C.borderGold,
        marginBottom: 16,
        paddingBottom: 8,
      }}
    >
      <Text
        style={{
          fontFamily: Fonts?.serifSemiBold,
          fontSize: 12,
          color: C.gold,
          textTransform: 'uppercase',
          letterSpacing: 3,
        }}
      >
        {children}
      </Text>
    </View>
  );
}

function StatRow({ label, value }: { label: string; value: string | number }) {
  return (
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        paddingVertical: 6,
        borderBottomWidth: 1,
        borderStyle: 'dotted',
        borderBottomColor: C.borderGoldFaint,
      }}
    >
      <Text style={{ fontSize: 12, color: C.textDim }}>{label}</Text>
      <Text style={{ fontFamily: Fonts?.serifSemiBold, fontSize: 14, color: C.text }}>{value}</Text>
    </View>
  );
}

function CatalogCard({
  spineColor,
  children,
  highlight,
}: {
  spineColor: string;
  children: React.ReactNode;
  highlight?: boolean;
}) {
  return (
    <View
      style={{
        backgroundColor: C.parchment,
        borderRadius: 2,
        flexDirection: 'row',
        overflow: 'hidden',
        shadowColor: '#000',
        shadowOffset: { width: 2, height: 2 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 4,
        ...(highlight ? { borderWidth: 2, borderColor: C.gold } : {}),
      }}
    >
      <View style={{ width: 8, backgroundColor: spineColor }} />
      <View style={{ flex: 1, padding: 20 }}>{children}</View>
    </View>
  );
}

function CatalogField({
  label,
  value,
  isTitle,
}: {
  label: string;
  value: string;
  isTitle?: boolean;
}) {
  return (
    <View style={{ marginBottom: 10 }}>
      <Text
        style={{
          fontSize: 9,
          textTransform: 'uppercase',
          letterSpacing: 2,
          color: C.parchmentMuted,
          marginBottom: 2,
        }}
      >
        {label}
      </Text>
      <Text
        style={
          isTitle
            ? { fontFamily: Fonts?.serifBold, fontSize: 20, color: C.parchmentText, lineHeight: 24 }
            : { fontSize: 14, color: C.parchmentText }
        }
      >
        {value}
      </Text>
    </View>
  );
}

function ProgressRing({ progress }: { progress: number }) {
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = `${(progress / 100) * circumference} ${circumference}`;

  return (
    <View style={{ alignItems: 'center', paddingVertical: 16 }}>
      <Svg width={120} height={120} viewBox="0 0 100 100">
        <Circle cx={50} cy={50} r={radius} fill="none" stroke={C.borderGoldFaint} strokeWidth={4} />
        <Circle
          cx={50}
          cy={50}
          r={radius}
          fill="none"
          stroke={C.gold}
          strokeWidth={4}
          strokeLinecap="round"
          strokeDasharray={strokeDasharray}
          rotation={-90}
          origin="50, 50"
        />
        <SvgText
          x={50}
          y={46}
          fill={C.gold}
          fontSize={20}
          fontWeight="700"
          textAnchor="middle"
          alignmentBaseline="middle"
        >
          {progress}%
        </SvgText>
        <SvgText
          x={50}
          y={62}
          fill={C.textDim}
          fontSize={10}
          textAnchor="middle"
          alignmentBaseline="middle"
          letterSpacing={1}
        >
          complete
        </SvgText>
      </Svg>
    </View>
  );
}

export default function DesignExample() {
  const data = SAMPLE_DATA;
  const maxVotes = Math.max(...data.votingCandidates.map((c) => c.votes));

  return (
    <View style={{ backgroundColor: C.bg }}>
      {/* HEADER */}
      <View
        style={{
          paddingTop: 60,
          paddingBottom: 48,
          paddingHorizontal: 24,
          alignItems: 'center',
          borderBottomWidth: 1,
          borderBottomColor: C.borderGold,
        }}
      >
        <Text style={{ fontSize: 48, color: C.gold, lineHeight: 56, marginBottom: 12 }}>
          {'\u2619'}
        </Text>
        <Text
          style={{
            fontFamily: Fonts?.serifBold,
            fontSize: 36,
            color: C.gold,
            letterSpacing: 1.5,
            textAlign: 'center',
            marginBottom: 8,
          }}
        >
          {data.club.name}
        </Text>
        <Text
          style={{
            fontFamily: Fonts?.serif,
            fontSize: 18,
            fontStyle: 'italic',
            color: C.goldMuted,
            textAlign: 'center',
            marginBottom: 12,
          }}
        >
          {data.club.motto}
        </Text>
        <Text
          style={{
            fontSize: 11,
            letterSpacing: 2,
            color: C.textDim,
            textTransform: 'uppercase',
            textAlign: 'center',
          }}
        >
          Est. {data.club.founded} {'\u00B7'} Cycle {data.club.currentCycle} {'\u00B7'}{' '}
          {data.club.totalBooksRead} volumes catalogued
        </Text>
      </View>

      {/* SIDEBAR SECTIONS (stacked on mobile) */}
      <View
        style={{
          backgroundColor: C.sidebarBg,
          padding: 24,
          borderBottomWidth: 1,
          borderBottomColor: C.borderGoldSubtle,
        }}
      >
        {/* Registry */}
        <View style={{ marginBottom: 32 }}>
          <SidebarHeading>Registry</SidebarHeading>
          <StatRow label="Members" value={data.club.memberCount} />
          <StatRow label="Volumes Read" value={data.club.totalBooksRead} />
          <StatRow label="Avg. Rating" value={`${data.club.averageRating}/5`} />
          <StatRow label="Next Convocation" value={data.club.nextMeetingDate} />
        </View>

        {/* Enrollment Register */}
        <View style={{ marginBottom: 32 }}>
          <SidebarHeading>Enrollment Register</SidebarHeading>
          {data.members.map((m) => (
            <View
              key={m.id}
              style={{
                paddingVertical: 10,
                borderBottomWidth: 1,
                borderStyle: 'dotted',
                borderBottomColor: C.borderGoldFaint,
              }}
            >
              <Text
                style={{
                  fontSize: 15,
                  letterSpacing: 1,
                  color: C.text,
                  textTransform: 'uppercase',
                }}
              >
                {m.name}
              </Text>
              <Text
                style={{
                  fontFamily: Fonts?.serif,
                  fontStyle: 'italic',
                  fontSize: 12,
                  color: C.gold,
                  textTransform: 'capitalize',
                }}
              >
                {m.role}
              </Text>
              <Text style={{ fontSize: 11, color: C.textDark, marginTop: 2 }}>
                Admitted {m.joinedDate} {'\u00B7'} {m.booksRead} vol.
              </Text>
            </View>
          ))}
        </View>

        {/* Reading Progress */}
        <View>
          <SidebarHeading>Reading Progress</SidebarHeading>
          <ProgressRing progress={data.currentBook.progress} />
          <StatRow label="Pages" value={data.currentBook.pageCount} />
          <StatRow label="Due" value={data.currentBook.dueDate} />
        </View>
      </View>

      {/* MAIN CONTENT */}
      <View style={{ padding: 24 }}>
        {/* Now Reading */}
        <View style={{ marginBottom: 48 }}>
          <SectionHeading>Now Reading</SectionHeading>
          <CatalogCard spineColor={data.currentBook.coverColor}>
            <CatalogField label="Title" value={data.currentBook.title} isTitle />
            <CatalogField label="Author" value={data.currentBook.author} />
            <View style={{ flexDirection: 'row', gap: 32 }}>
              <CatalogField label="Genre" value={data.currentBook.genre} />
              <CatalogField label="Pages" value={String(data.currentBook.pageCount)} />
            </View>
            <View
              style={{
                alignSelf: 'flex-start',
                marginTop: 8,
                paddingVertical: 4,
                paddingHorizontal: 12,
                borderWidth: 2,
                borderColor: C.stamp,
                transform: [{ rotate: '-2deg' }],
              }}
            >
              <Text
                style={{
                  fontSize: 10,
                  fontWeight: '700',
                  letterSpacing: 1.5,
                  color: C.stamp,
                  opacity: 0.7,
                }}
              >
                CURRENTLY IN CIRCULATION
              </Text>
            </View>
          </CatalogCard>
        </View>

        {/* Nominations */}
        <View style={{ marginBottom: 48 }}>
          <SectionHeading>Nominations for Next Volume</SectionHeading>
          <View style={{ gap: 16 }}>
            {data.votingCandidates.map((c) => (
              <CatalogCard key={c.id} spineColor={c.coverColor} highlight={c.votes === maxVotes}>
                <View style={{ position: 'absolute', top: 12, right: 16 }}>
                  <Text
                    style={{
                      fontFamily: Fonts?.serifBold,
                      fontSize: 36,
                      color: C.gold,
                      lineHeight: 40,
                    }}
                  >
                    {c.votes}
                  </Text>
                </View>
                <CatalogField label="Title" value={c.title} isTitle />
                <CatalogField label="Author" value={c.author} />
                <CatalogField label="Genre" value={c.genre} />
                <Text
                  style={{
                    fontSize: 13,
                    lineHeight: 20,
                    color: C.parchmentDark,
                    fontStyle: 'italic',
                    paddingRight: 48,
                    marginBottom: 8,
                  }}
                >
                  {c.synopsis}
                </Text>
                <Text style={{ fontSize: 11, color: C.parchmentMuted, letterSpacing: 0.5 }}>
                  Nominated by {c.nominatedBy} {'\u00B7'} {c.pageCount} pages
                </Text>
              </CatalogCard>
            ))}
          </View>
        </View>

        {/* Discussion */}
        <View style={{ marginBottom: 48 }}>
          <SectionHeading>Marginalia & Discourse</SectionHeading>
          <View style={{ gap: 20 }}>
            {data.currentBook.discussion.map((d) => (
              <View
                key={d.id}
                style={{
                  padding: 20,
                  backgroundColor: C.discussionBg,
                  borderLeftWidth: 3,
                  borderLeftColor: C.borderGoldAccent,
                  borderTopRightRadius: 2,
                  borderBottomRightRadius: 2,
                }}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: 8,
                  }}
                >
                  <Text
                    style={{
                      fontSize: 14,
                      letterSpacing: 1,
                      color: C.gold,
                      textTransform: 'uppercase',
                    }}
                  >
                    {d.author}
                  </Text>
                  <Text style={{ fontSize: 11, color: C.textDark, fontStyle: 'italic' }}>
                    {d.timestamp}
                  </Text>
                </View>
                <Text
                  style={{
                    fontFamily: Fonts?.serifSemiBold,
                    fontSize: 18,
                    color: C.discussionTitle,
                    marginBottom: 8,
                  }}
                >
                  {d.title}
                </Text>
                <Text style={{ fontSize: 14, lineHeight: 22, color: C.discussionBody }}>
                  {d.body}
                </Text>
                <Text style={{ marginTop: 12, fontSize: 11, color: C.textDark, letterSpacing: 1 }}>
                  {d.replies} replies {'\u00B7'} {d.likes} endorsements
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* Reading History */}
        <View style={{ marginBottom: 48 }}>
          <SectionHeading>Archive of Volumes Past</SectionHeading>
          <View style={{ gap: 10 }}>
            {data.readingHistory.map((h) => (
              <View
                key={h.id}
                style={{
                  backgroundColor: C.parchment,
                  flexDirection: 'row',
                  overflow: 'hidden',
                  borderRadius: 2,
                  shadowColor: '#000',
                  shadowOffset: { width: 1, height: 1 },
                  shadowOpacity: 0.3,
                  shadowRadius: 4,
                  elevation: 2,
                }}
              >
                <View style={{ width: 6, backgroundColor: h.coverColor }} />
                <View style={{ flex: 1, padding: 12, gap: 2 }}>
                  <Text
                    style={{
                      fontFamily: Fonts?.serif,
                      fontSize: 11,
                      color: C.parchmentMuted,
                      letterSpacing: 1.5,
                      textTransform: 'uppercase',
                    }}
                  >
                    Vol. {h.cycle}
                  </Text>
                  <Text
                    style={{
                      fontFamily: Fonts?.serifBold,
                      fontSize: 15,
                      color: C.parchmentText,
                      lineHeight: 18,
                    }}
                  >
                    {h.title}
                  </Text>
                  <Text style={{ fontSize: 12, color: C.parchmentAuthor, fontStyle: 'italic' }}>
                    {h.author}
                  </Text>
                  <Text style={{ fontSize: 10, color: C.parchmentMuted, marginTop: 4 }}>
                    {h.readDate} {'\u00B7'} {h.rating}/5 {'\u00B7'} Selected by {h.pickedBy}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>
      </View>

      {/* FOOTER */}
      <View
        style={{
          alignItems: 'center',
          paddingVertical: 48,
          paddingHorizontal: 24,
          borderTopWidth: 1,
          borderTopColor: C.borderGold,
        }}
      >
        <Text style={{ fontSize: 24, color: C.gold, opacity: 0.5, marginBottom: 12 }}>
          {'\u2733'}
        </Text>
        <Text
          style={{
            fontFamily: Fonts?.serifSemiBold,
            fontSize: 16,
            color: C.gold,
            letterSpacing: 1,
          }}
        >
          {data.club.name} {'\u00B7'} Est. {data.club.founded}
        </Text>
        <Text style={{ fontSize: 12, color: C.textDark, marginTop: 8, letterSpacing: 0.8 }}>
          Average rating: {data.club.averageRating}/5 {'\u00B7'} Next meeting:{' '}
          {data.club.nextMeetingDate}
        </Text>
      </View>
    </View>
  );
}
